import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IoMdArrowBack } from "react-icons/io";
import { FiRefreshCw, FiSearch } from "react-icons/fi";
import DataTable from "react-data-table-component";
import Loader from "../../components/loader/loader";
import { getAdminBillingTransactions } from "../../api/context/billing";

const transactionsDatatableCustomStyles = {
    header: {
        style: {
            fontWeight: "600",
            fontSize: "22px",
            color: "#000",
        },
    },
    headCells: {
        style: {
            display: "flex",
            justifyContent: "center",
            fontWeight: "600",
            fontSize: "13px",
            padding: "16px 8px",
            backgroundColor: "#f9f9f9",
        },
    },
    cells: {
        style: {
            display: "flex",
            fontSize: "12px",
            fontWeight: "500",
            padding: "12px",
            justifyContent: "center",
            textAlign: "center",
        },
    },
};

const TYPE_OPTIONS = [
    { value: "topup_purchase", label: "Top-up Purchase" },
    { value: "subscription_reset", label: "Subscription Reset" },
    { value: "subscription_grant", label: "Subscription Grant" },
    { value: "listing_consume", label: "Listing Consume" },
    { value: "refund", label: "Refund" },
];

const SOURCE_OPTIONS = [
    { value: "wallet", label: "Wallet" },
    { value: "subscription", label: "Subscription" },
];

const TYPE_BADGE_STYLES = {
    topup_purchase: { bg: "#dbeafe", color: "#1d4ed8" },
    subscription_reset: { bg: "#ede9fe", color: "#6d28d9" },
    subscription_grant: { bg: "#e0e7ff", color: "#4338ca" },
    listing_consume: { bg: "#fef3c7", color: "#b45309" },
    refund: { bg: "#fee2e2", color: "#b91c1c" },
    default: { bg: "#f1f5f9", color: "#475569" },
};

const SOURCE_BADGE_STYLES = {
    wallet: { bg: "#dcfce7", color: "#15803d" },
    subscription: { bg: "#ede9fe", color: "#6d28d9" },
    default: { bg: "#f1f5f9", color: "#475569" },
};

const formatCurrency = (value) => {
    const number = Number(value || 0);
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(number);
};

const formatNumber = (value) => new Intl.NumberFormat("en-IN").format(Number(value || 0));

const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const humanizeType = (type) => {
    if (!type) return "—";
    return type
        .split("_")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
};

const AiListingTransactions = () => {
    const navigate = useNavigate();

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        return {
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
        };
    };

    const getTodayDate = () => new Date().toISOString().split("T")[0];

    const [filters, setFilters] = useState({
        ...getDefaultDates(),
        type: "",
        source: "",
        search: "",
    });
    const [searchInput, setSearchInput] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchTransactions = useCallback(
        async (isRefresh = false, overrides = {}) => {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            const effective = { ...filters, ...overrides };
            const requestPage = overrides.page ?? page;
            const requestPageSize = overrides.pageSize ?? pageSize;

            const params = {
                page: requestPage,
                pageSize: requestPageSize,
            };
            if (effective.startDate) params.startDate = effective.startDate;
            if (effective.endDate) params.endDate = effective.endDate;
            if (effective.type) params.type = effective.type;
            if (effective.source) params.source = effective.source;
            if (effective.search) params.search = effective.search;

            try {
                const response = await getAdminBillingTransactions(params);
                if (response?.data?.statusCode === 200 || response?.status === 200) {
                    const payload = response?.data?.data || response?.data;
                    setData(payload);
                } else {
                    setData(null);
                    toast.error("Failed to fetch transactions");
                }
            } catch (error) {
                console.error("Error fetching admin transactions:", error);
                if (error?.response?.status === 403) {
                    toast.error("You don't have permission to view transactions");
                } else {
                    toast.error("Failed to fetch transactions");
                }
                setData(null);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters, page, pageSize]
    );

    useEffect(() => {
        fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    useEffect(() => {
        setPage(1);
        const timer = setTimeout(() => fetchTransactions(true, { page: 1 }), 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.startDate, filters.endDate, filters.type, filters.source, filters.search]);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();
        if (key === "startDate") {
            if (value > today) return toast.error("Start date cannot be in the future");
            if (filters.endDate && value > filters.endDate) return toast.error("Start date cannot be after end date");
        }
        if (key === "endDate") {
            if (value > today) return toast.error("End date cannot be in the future");
            if (filters.startDate && value < filters.startDate) return toast.error("End date cannot be before start date");
        }
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleSearchChange = (value) => {
        setSearchInput(value);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleClearFilters = () => {
        const defaults = getDefaultDates();
        setFilters({ ...defaults, type: "", source: "", search: "" });
        setSearchInput("");
        setPage(1);
    };

    const isFilterActive = useMemo(() => {
        const defaults = getDefaultDates();
        return (
            filters.startDate !== defaults.startDate ||
            filters.endDate !== defaults.endDate ||
            filters.type !== "" ||
            filters.source !== "" ||
            filters.search !== ""
        );
    }, [filters]);

    const transactions = Array.isArray(data?.transactions) ? data.transactions : [];
    const totalRows = data?.pagination?.total || 0;

    const columns = useMemo(
        () => [
            {
                name: "Vendor",
                center: true,
                minWidth: "220px",
                grow: 2,
                cell: (row) => {
                    const vendor = row.vendor || {};
                    return (
                        <div className="d-flex align-items-center justify-content-center gap-2" style={{ width: "100%" }}>
                            {vendor.image ? (
                                <img
                                    src={vendor.image}
                                    alt={vendor.storeName}
                                    className="rounded-circle flex-shrink-0"
                                    style={{ width: 32, height: 32, objectFit: "cover", border: "1px solid #eef2f7" }}
                                />
                            ) : (
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-muted"
                                    style={{ width: 32, height: 32, background: "#f1f5f9", fontSize: 12 }}
                                >
                                    {(vendor.storeName || "?").charAt(0).toUpperCase()}
                                </div>
                            )}
                            <span
                                className="fw-semibold text-truncate"
                                style={{ maxWidth: 160 }}
                                title={vendor.storeName}
                            >
                                {vendor.storeName || "—"}
                            </span>
                        </div>
                    );
                },
            },
            {
                name: "Type",
                center: true,
                minWidth: "160px",
                cell: (row) => {
                    const style = TYPE_BADGE_STYLES[row.type] || TYPE_BADGE_STYLES.default;
                    return (
                        <span
                            className="badge rounded-pill"
                            style={{
                                background: style.bg,
                                color: style.color,
                                fontWeight: 600,
                                fontSize: 11,
                                padding: "6px 10px",
                            }}
                        >
                            {humanizeType(row.type)}
                        </span>
                    );
                },
            },
            {
                name: "Source",
                center: true,
                minWidth: "120px",
                cell: (row) => {
                    if (!row.source) return <span className="text-muted">—</span>;
                    const style = SOURCE_BADGE_STYLES[row.source] || SOURCE_BADGE_STYLES.default;
                    return (
                        <span
                            className="badge rounded-pill"
                            style={{
                                background: style.bg,
                                color: style.color,
                                fontWeight: 600,
                                fontSize: 11,
                                padding: "6px 10px",
                            }}
                        >
                            {humanizeType(row.source)}
                        </span>
                    );
                },
            },
            {
                name: "Item",
                center: true,
                minWidth: "180px",
                grow: 2,
                cell: (row) => {
                    const itemName =
                        row?.topupPack?.name ||
                        row?.subscription?.plan?.name ||
                        row?.product?.productName ||
                        row?.description ||
                        "—";
                    return (
                        <span className="text-truncate" style={{ maxWidth: 200 }} title={itemName}>
                            {itemName}
                        </span>
                    );
                },
            },
            {
                name: "Credits (Δ)",
                center: true,
                minWidth: "110px",
                cell: (row) => {
                    const delta = Number(row.amount || 0);
                    const positive = delta > 0;
                    const negative = delta < 0;
                    return (
                        <span
                            className="fw-semibold"
                            style={{ color: positive ? "#15803d" : negative ? "#b91c1c" : "#475569" }}
                        >
                            {positive ? "+" : ""}
                            {formatNumber(delta)}
                        </span>
                    );
                },
            },
            {
                name: "Balance After",
                center: true,
                minWidth: "120px",
                cell: (row) => (
                    <span className="text-muted">
                        {row.balanceAfter != null ? formatNumber(row.balanceAfter) : "—"}
                    </span>
                ),
            },
            {
                name: "Amount",
                center: true,
                minWidth: "120px",
                cell: (row) =>
                    Number(row.amountInr || 0) > 0 ? (
                        <span className="fw-bold">{formatCurrency(row.amountInr)}</span>
                    ) : (
                        <span className="text-muted">—</span>
                    ),
            },
            {
                name: "Date",
                center: true,
                minWidth: "170px",
                cell: (row) => (
                    <span className="text-muted" style={{ fontSize: 12 }}>
                        {formatDateTime(row.createdAt)}
                    </span>
                ),
            },
        ],
        [page, pageSize]
    );

    const controlHeight = 40;

    return (
        <div className="container-fluid p-4 min-vh-100">
            {/* Header (two rows) */}
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,
                    background: "#f9fafb",
                    margin: "-44px -48px 20px",
                    padding: "40px 48px 24px",
                }}
            >
                {/* Row 1 — title + date range + refresh */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center mb-0">
                        <div
                            onClick={() => navigate(-1)}
                            style={{
                                cursor: "pointer",
                                fontSize: "24px",
                                fontWeight: "800",
                                display: "flex",
                                alignItems: "center",
                                marginRight: "15px",
                            }}
                        >
                            <IoMdArrowBack className="icon" />
                        </div>
                        <h4 className="mb-0 fw-bold">All Transactions</h4>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <input
                            type="date"
                            className="form-control"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange("startDate", e.target.value)}
                            style={{ width: 150, height: controlHeight, fontSize: 13 }}
                            max={filters.endDate || getTodayDate()}
                        />

                        <div className="text-muted small">to</div>

                        <input
                            type="date"
                            className="form-control"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange("endDate", e.target.value)}
                            style={{ width: 150, height: controlHeight, fontSize: 13 }}
                            min={filters.startDate || undefined}
                            max={getTodayDate()}
                        />

                        <button
                            onClick={() => fetchTransactions(true)}
                            className="btn border border-secondary bg-transparent rounded-3 d-flex align-items-center gap-2 px-3"
                            style={{ height: controlHeight }}
                            disabled={refreshing}
                        >
                            <FiRefreshCw
                                className="text-secondary"
                                size={18}
                                style={{ animation: refreshing ? "spin 1s linear infinite" : "none" }}
                            />
                            <span className="text-secondary">{refreshing ? "Refreshing..." : "Refresh"}</span>
                        </button>
                    </div>
                </div>

                {/* Row 2 — search + type + source + clear */}
                <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
                    <div className="position-relative" style={{ flex: "1 1 320px", minWidth: 260 }}>
                        <FiSearch
                            className="position-absolute text-muted"
                            style={{ top: "50%", left: 12, transform: "translateY(-50%)", fontSize: 15 }}
                        />
                        <input
                            type="text"
                            className="form-control"
                            style={{ paddingLeft: 34, height: controlHeight, fontSize: 13, borderRadius: 8, width: "100%" }}
                            placeholder="Search vendor by store name"
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>

                    <select
                        className="form-select flex-shrink-0"
                        style={{ width: 200, height: controlHeight, fontSize: 13 }}
                        value={filters.type}
                        onChange={(e) => handleFilterChange("type", e.target.value)}
                    >
                        <option value="">All types</option>
                        {TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    <select
                        className="form-select flex-shrink-0"
                        style={{ width: 160, height: controlHeight, fontSize: 13 }}
                        value={filters.source}
                        onChange={(e) => handleFilterChange("source", e.target.value)}
                    >
                        <option value="">All sources</option>
                        {SOURCE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    {isFilterActive && (
                        <button
                            onClick={handleClearFilters}
                            className="btn btn-outline-secondary flex-shrink-0"
                            style={{ height: controlHeight, fontSize: 13 }}
                            disabled={refreshing}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Transactions table */}
            <div className="shadow-none border border-blue-grey-200 rounded-3" style={{ overflowX: "auto" }}>
                <DataTable
                    columns={columns}
                    data={transactions}
                    progressPending={loading || refreshing}
                    progressComponent={
                        <div style={{ height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Loader />
                        </div>
                    }
                    noDataComponent={
                        <div className="d-flex flex-column align-items-center justify-content-center text-center text-muted p-5">
                            <h6 className="fw-bold mb-2">No transactions found</h6>
                            <small className="mb-0" style={{ maxWidth: 420 }}>
                                Try adjusting the filters or expanding the date range.
                            </small>
                        </div>
                    }
                    highlightOnHover
                    pointerOnHover
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={pageSize}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    paginationDefaultPage={page}
                    onChangeRowsPerPage={(newPerPage, newPage) => {
                        setPageSize(newPerPage);
                        setPage(newPage);
                    }}
                    onChangePage={(newPage) => setPage(newPage)}
                    customStyles={transactionsDatatableCustomStyles}
                />
            </div>

            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

export default AiListingTransactions;
